import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainRoutes } from "common/config";
import {
  ArrowBack as ArrowLeftIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Delete as TrashIcon,
  Add as PlusIcon,
  Cancel as XCircleIcon,
  Warning as AlertCircleIcon,
  AutorenewOutlined as Loader2Icon,
  AutoAwesome as SparklesIcon,
  Settings as SettingsIcon,
  Science as FlaskConicalIcon,
  MyLocation as TargetIcon
} from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import { Button } from "common/components";
import { ExternalApiClient } from "common/services/external-api-client.service";

import Modal from "common/components/base/modal/Modal";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import {
  FieldInput,
  TestCase,
  Tool
} from "modules/configuration-hub/data/models/configuration-hub.model";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import "./ToolDevelopment.scss";

// Type definitions for API responses
interface ToolExistenceResponse {
  isSucceed: boolean;
  message: string;
}

interface PollingResponse {
  name: string;
  instanceId: string;
  runtimeStatus: string;
  input: string;
  customStatus: any;
  output: {
    exists: boolean;
    matched_tool_name: string;
    comment: string;
  };
  createdTime: string;
  lastUpdatedTime: string;
}

const ToolDevelopment = () => {
  const navigate = useNavigate();

  // Tool configuration
  const [toolName, setToolName] = useState("");
  const [toolDescription, setToolDescription] = useState("");
  const [toolCreated, setToolCreated] = useState(false);
  const [functionName, setFunctionName] = useState("");
  const [inputFields, setInputFields] = useState<string[]>([]);

  // Agent loading state
  const [isCreatingTool, setIsCreatingTool] = useState(false);
  const [agentStep, setAgentStep] = useState("");

  // Validation state
  const [batchId, setBatchId] = useState("");
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [validationRun, setValidationRun] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  // Tool existence check state
  const [showExistenceModal, setShowExistenceModal] = useState(false);
  const [existenceModalMessage, setExistenceModalMessage] = useState("");
  const [matchedToolName, setMatchedToolName] = useState("");

  const generateDocId = () => {
    return "TC-" + Math.random().toString(36).substring(2, 9).toUpperCase();
  };

  // API function to check tool existence
  const checkToolExistence = async (name: string, description: string) => {
    try {
      const response = await ExternalApiClient.post(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/checkExistence`,
        {
          toolName: name,
          toolDescription: description
        }
      );
      return response;
    } catch (error) {
      console.error("Error checking tool existence:", error);
      throw error;
    }
  };

  // Function to poll the URL for existence check result
  const pollExistenceResult = async (url: string): Promise<PollingResponse> => {
    try {
      let attempts = 0;
      const maxAttempts = 10;

      while (attempts < maxAttempts) {
        try {
          const response = await ExternalApiClient.get(url);
          const responseData = response.value as PollingResponse;

          if (responseData && responseData.runtimeStatus === "Completed") {
            return responseData;
          }
        } catch (pollError) {
          // Continue to the next attempt instead of breaking
        }

        // Wait 2 seconds before next attempt
        await new Promise((resolve) => setTimeout(resolve, 2000));
        attempts++;
      }

      throw new Error("Polling timeout: Unable to get existence check result");
    } catch (error) {
      console.error("Error polling existence result:", error);
      throw error;
    }
  };

  // API function to create agentic tool
  const createAgenticTool = async (name: string, description: string) => {
    try {
      const response = await ExternalApiClient.post(
        `${process.env.REACT_APP_ENG_API_URL}/api/Documents/AgenticTool`,
        {
          name: name,
          description: description
        }
      );
      return response;
    } catch (error) {
      console.error("Error creating agentic tool:", error);
      throw error;
    }
  };

  const handleCreateTool = async () => {
    if (!toolName.trim() || !toolDescription.trim()) {
      ToastMessageUtil.show(
        "Please fill in all required fields",
        ToastVariant.ERROR
      );
      return;
    }

    if (isCreatingTool) {
      return;
    }

    setIsCreatingTool(true);

    try {
      // Step 1: Check tool existence
      setAgentStep("Checking if tool already exists...");

      const existenceResponse = await checkToolExistence(
        toolName,
        toolDescription
      );

      const existenceData = existenceResponse.value as ToolExistenceResponse;

      if (existenceData && existenceData.isSucceed && existenceData.message) {
        // Poll the URL to get the final result
        setAgentStep("Validating tool uniqueness...");

        const pollResult = await pollExistenceResult(existenceData.message);

        if (pollResult.output && pollResult.output.exists) {
          // Tool already exists - show modal and stop process
          setMatchedToolName(pollResult.output.matched_tool_name || toolName);
          setExistenceModalMessage(
            pollResult.output.comment ||
              "Tool already exists with similar configuration."
          );
          setShowExistenceModal(true);
          setIsCreatingTool(false);
          return;
        }
      }

      // Tool doesn't exist, proceed with creation
      // Step 2: Analyzing
      setAgentStep("Analyzing tool requirements...");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Step 3: Generating code
      setAgentStep("Agent generating tool code...");
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Auto-generate function name
      const generatedFunctionName = toolName
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "_")
        .concat("_tool");

      // Simulate LLM generating input fields
      const generatedInputFields =
        extractInputFieldsFromDescription(toolDescription);

      // Step 4: Saving to API
      setAgentStep("Saving tool configuration to API...");
      const apiResponse = await createAgenticTool(toolName, toolDescription);

      if (apiResponse.statusCode !== 200 && apiResponse.statusCode !== 201) {
        throw new Error("Failed to save tool to API");
      }

      // Step 5: Registering
      setAgentStep("Registering tool in pipeline...");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Generate batch ID
      const newBatchId = `BATCH_${Date.now().toString().slice(-6)}`;

      setFunctionName(generatedFunctionName);
      setInputFields(generatedInputFields);
      setBatchId(newBatchId);
      setIsCreatingTool(false);
      setToolCreated(true);

      // Note: Tool is saved to API only.
      // The AvailableTools component will fetch it from API to avoid duplicates.
      // No need to save to localStorage since API is the source of truth.

      ToastMessageUtil.show(
        "Tool created and saved successfully!",
        ToastVariant.SUCCESS
      );
    } catch (error) {
      console.error("Error creating tool:", error);
      setIsCreatingTool(false);
      ToastMessageUtil.show(
        "Failed to create tool. Please try again.",
        ToastVariant.ERROR
      );
    }
  };

  // Mock LLM field extraction
  const extractInputFieldsFromDescription = (description: string): string[] => {
    const lowerDesc = description.toLowerCase();

    if (
      lowerDesc.includes("subtotal") &&
      lowerDesc.includes("tax") &&
      lowerDesc.includes("total")
    ) {
      return ["subtotal_field", "tax_field", "total_field"];
    } else if (lowerDesc.includes("party") || lowerDesc.includes("name")) {
      return ["party_name_field"];
    } else if (lowerDesc.includes("email")) {
      return ["email_field"];
    } else if (lowerDesc.includes("date")) {
      return ["date_field"];
    } else if (lowerDesc.includes("amount")) {
      return ["amount_field"];
    } else if (lowerDesc.includes("state") && lowerDesc.includes("country")) {
      return ["state_field", "country_field"];
    } else {
      return ["field_1", "field_2"];
    }
  };

  const handleAddTestCase = () => {
    const newDocId = generateDocId();
    const documentType = `Test Case ${testCases.length + 1}`;

    const newTestCase: TestCase = {
      doc_id: newDocId,
      document_type: documentType,
      fields: inputFields.map((fieldName) => ({
        field_name: fieldName,
        field_value: ""
      }))
    };

    setTestCases([...testCases, newTestCase]);
  };

  const handleRemoveTestCase = (docId: string) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.doc_id !== docId));
    } else {
      ToastMessageUtil.show(
        "At least one test case is required",
        ToastVariant.ERROR
      );
    }
  };

  const handleUpdateField = (
    docId: string,
    fieldIndex: number,
    key: "field_name" | "field_value",
    value: string
  ) => {
    setTestCases(
      testCases.map((tc) => {
        if (tc.doc_id === docId) {
          const updatedFields = [...tc.fields];
          updatedFields[fieldIndex] = {
            ...updatedFields[fieldIndex],
            [key]: value
          };
          return { ...tc, fields: updatedFields };
        }
        return tc;
      })
    );
  };

  const handleValidateTool = async () => {
    // Check if all fields have values
    const hasEmptyFields = testCases.some((tc) =>
      tc.fields.some((field) => !field.field_value.trim())
    );

    if (hasEmptyFields) {
      ToastMessageUtil.show(
        "Please fill in all field values",
        ToastVariant.ERROR
      );
      return;
    }

    setIsValidating(true);

    // Simulate validation process
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Validate each test case (test case level, not field level)
    const validatedTestCases = testCases.map((tc) => {
      // Mock validation: randomly pass or fail the entire test case
      const isPassed = Math.random() > 0.3; // 70% pass rate

      return {
        ...tc,
        status: isPassed ? ("Pass" as const) : ("Fail" as const),
        comment: isPassed
          ? "All field validations passed successfully"
          : "Validation failed: Inconsistency detected in field values"
      };
    });

    setTestCases(validatedTestCases);
    setValidationRun(true);
    setIsValidating(false);

    const passCount = validatedTestCases.filter(
      (tc) => tc.status === "Pass"
    ).length;

    if (passCount === validatedTestCases.length) {
      ToastMessageUtil.show("All test cases passed!", ToastVariant.SUCCESS);
    } else {
      ToastMessageUtil.show(
        `${passCount} of ${validatedTestCases.length} test cases passed`,
        ToastVariant.WARNING
      );
    }
  };

  const handleDownloadResults = () => {
    if (!validationRun) {
      ToastMessageUtil.show("Please run validation first", ToastVariant.ERROR);
      return;
    }

    // Generate CSV matching the database structure
    const headers = [
      "batch_id",
      "doc_id",
      "document_type",
      "field_name",
      "field_value",
      "status",
      "comment"
    ];
    const rows: string[][] = [];

    testCases.forEach((tc) => {
      tc.fields.forEach((field) => {
        rows.push([
          batchId,
          tc.doc_id,
          tc.document_type,
          field.field_name,
          field.field_value,
          tc.status || "",
          tc.comment || ""
        ]);
      });
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${batchId}_validation_results.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    ToastMessageUtil.show(
      "Results downloaded successfully",
      ToastVariant.SUCCESS
    );
  };

  const handleReworkTool = () => {
    setToolCreated(false);
    setValidationRun(false);
    setTestCases([]);
    setInputFields([]);
    setToolName("");
    setToolDescription("");
    ToastMessageUtil.show(
      "Reworking tool - update your configuration",
      ToastVariant.INFO
    );
  };

  const handleApproveTool = () => {
    if (!validationRun) {
      ToastMessageUtil.show(
        "Please run validation before approving",
        ToastVariant.ERROR
      );
      return;
    }

    const allPassed = testCases.every((tc) => tc.status === "Pass");

    if (!allPassed) {
      const failCount = testCases.filter((tc) => tc.status === "Fail").length;
      ToastMessageUtil.show(
        `${failCount} test case(s) failed. Review before approving.`,
        ToastVariant.WARNING
      );
    }

    // Save tool to localStorage with approved status
    const newTool: Tool = {
      id: `custom-${Date.now()}`,
      name: functionName,
      displayName: toolName,
      description: toolDescription,
      isCustom: true,
      functionName: functionName,
      inputFields: inputFields,
      status: "approved"
    };

    const existingTools = JSON.parse(
      localStorage.getItem("customValidationTools") || "[]"
    );
    const updatedTools = [...existingTools, newTool];
    localStorage.setItem("customValidationTools", JSON.stringify(updatedTools));

    ToastMessageUtil.show(
      "Tool approved and deployed to quality check pipeline!",
      ToastVariant.SUCCESS
    );
    setTimeout(() => {
      navigate(MainRoutes.AVAILABLE_TOOLS);
    }, 1500);
  };

  const handleDeleteTool = () => {
    if (confirm("Are you sure you want to delete this tool?")) {
      ToastMessageUtil.show("Tool deleted successfully", ToastVariant.SUCCESS);
      setTimeout(() => {
        navigate(MainRoutes.AVAILABLE_TOOLS);
      }, 1000);
    }
  };

  const allPassed =
    validationRun &&
    testCases.length > 0 &&
    testCases.every((tc) => tc.status === "Pass");

  return (
    <>
      <HeaderNew showSearch={false} />

      {/* Tool Existence Modal */}
      <Modal
        show={showExistenceModal}
        onClose={() => setShowExistenceModal(false)}
        title="Tool Already Exists"
        size="md"
      >
        <div className="modal-content">
          <div className="modal-body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}
            >
              <AlertCircleIcon
                style={{ color: "#f59e0b", marginRight: "8px" }}
              />
              <h3 style={{ margin: 0 }}>
                Tool &quot;{matchedToolName}&quot; Already Exists
              </h3>
            </div>
            <p style={{ marginBottom: "20px", color: "#6b7280" }}>
              <span style={{ fontWeight: "bold" }}>Comment:</span>{" "}
              {existenceModalMessage}
            </p>
            <div
              className="modal-actions"
              style={{
                display: "flex",
                gap: "50px",
                justifyContent: "flex-end"
              }}
            >
              <button
                className="btn btn--primary btn--large"
                onClick={() => {
                  setShowExistenceModal(false);
                  // Reset form to allow user to modify
                  setToolName("");
                  setToolDescription("");
                }}
              >
                Create Different Tool
              </button>
              <button
                className="btn btn--primary btn--large"
                onClick={() => {
                  setShowExistenceModal(false);
                  navigate(MainRoutes.AVAILABLE_TOOLS);
                }}
              >
                View Existing Tools
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <div className="tool-development">
        <HubHeader
          className="header"
          title="Tool Development"
          description="Create and validate custom tools for your quality check pipeline"
          showBackButton={true}
          backRoute="/extraction-config/available-tools"
        />
        <div className="tool-development__container">
          {/* Back Button */}
          {/* <button
            className="tool-development__back-button"
            onClick={() => navigate(MainRoutes.AVAILABLE_TOOLS)}
          >
            <ArrowLeftIcon className="icon" />
            Back to Available Tools
          </button> */}

          {/* Header */}
          {/* <div className="tool-development__header">
            <div className="tool-development__header-title">
              <div className="tool-development__header-icon">
                <SettingsIcon />
              </div>
              <div>
                <h1>Tool Development</h1>
                <p className="tool-development__header-description">
                  Create and validate custom tools for your quality check
                  pipeline
                </p>
              </div>
            </div>
          </div> */}

          {/* Tool Configuration Card */}
          <div className="tool-development__section">
            <div className="tool-development__card">
              <div className="tool-development__card-header">
                <TargetIcon className="icon" />
                <h2>Tool Configuration</h2>
              </div>

              <div className="tool-development__form">
                <div className="tool-development__form-group">
                  <label className="tool-development__label">
                    Tool Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="input"
                    placeholder="e.g., Subtotal Tax Total Consistency Validator"
                    value={toolName}
                    onChange={(e) => setToolName(e.target.value)}
                    disabled={toolCreated || isCreatingTool}
                  />
                </div>

                <div className="tool-development__form-group">
                  <label className="tool-development__label">
                    Tool Description <span className="required">*</span>
                  </label>
                  <textarea
                    className="input tool-development__textarea"
                    placeholder="Describe what this tool validates (e.g., validates that subtotal + tax = total)"
                    value={toolDescription}
                    onChange={(e) => setToolDescription(e.target.value)}
                    disabled={toolCreated || isCreatingTool}
                    rows={4}
                  />
                </div>

                {!toolCreated && !isCreatingTool && (
                  <div className="tool-development__form-actions">
                    <button
                      className="btn btn--primary btn--large"
                      onClick={handleCreateTool}
                      disabled={isCreatingTool}
                    >
                      <SettingsIcon className="icon" />
                      Validate & Create Tool
                    </button>
                  </div>
                )}

                {/* Agent Loading State */}
                {isCreatingTool && (
                  <div className="tool-development__loading">
                    <div className="tool-development__loading-content">
                      <div className="tool-development__loading-icon">
                        <CircularProgress size={24} />
                      </div>
                      <div className="tool-development__loading-text">
                        <p className="primary">{agentStep}</p>
                        <p className="secondary">Please wait...</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tool Created Success */}
          {toolCreated && (
            <>
              {/* <div className="tool-development__section">
                <div className="tool-development__success-card">
                  <div className="tool-development__success-content">
                    <div className="tool-development__success-icon">
                      <CheckCircleIcon />
                    </div>
                    <div className="tool-development__success-text">
                      <h3>Tool Information</h3>
                      <div className="tool-development__info-grid">
                        <div className="tool-development__info-item">
                          <p className="label">Function Name</p>
                          <code className="value">{functionName}</code>
                        </div>
                        <div className="tool-development__info-item">
                          <p className="label">Input Fields</p>
                          <div className="tool-development__fields">
                            {inputFields.map((field, idx) => (
                              <span key={idx} className="field-tag">
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Warning Card */}
              <div className="tool-development__section">
                <div className="tool-development__warning-card">
                  <div className="tool-development__warning-content">
                    <div className="tool-development__warning-icon">
                      <AlertCircleIcon />
                    </div>
                    <div className="tool-development__warning-text">
                      <h3>Validation Required</h3>
                      <p>
                        Please navigate to the Available Tools page to validate
                        and approve this tool before it can be used in the
                        quality check pipeline.
                      </p>
                      <button
                        className="btn btn--primary"
                        onClick={() => navigate(MainRoutes.AVAILABLE_TOOLS)}
                      >
                        Go to Available Tools
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ToolDevelopment;
